import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n, { Translation } from "../../../../../hooks/useTranslations";
import { NormativeVersion } from "../../../../../http/patrol/types/normativeVersions";
import { useNormativeVersionsApi } from "../../../../../http/patrol/useNormativeVersions";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import AddNormativeVersionButton from "./AddNormativeVersionButton";
import NormativeVersionForm from "./NormativeVersionForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const NormativeVersions: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const { normativeId } = useParams<{ normativeId: string }>();
  const filters = useListFilters({ key: "normativeVersions" });
  const aborter = useRef(new AbortController());
  const normativeVersionsApi = useNormativeVersionsApi();
  const { setActions, setTitle } = useSearchContext();

  const updateVisibility = async (normativeId: string, id: string, visibility: VisibilityScopeValue) => {
    try {
      await normativeVersionsApi.updateAdminNormativeVersionVisibility(normativeId, id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "normativeVersions");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = normativeVersionsApi.useAdminNormativeVersions(normativeId!, query, aborter.current.signal);

  const [itemToEdit, setItemToEdit] = useState<NormativeVersion>();
  const [itemToDelete, setItemToDelete] = useState<NormativeVersion>();
  const { accessesAction, accessesDialog } = useEntityAccesses<NormativeVersion>("patrol.normative_version");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.common.versions);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.items.search} value={searchTerm} onChange={handleSearchChange} />
        <AddNormativeVersionButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <>
      <FlexContent padding>
        <Table<NormativeVersion>
          filters={filters}
          isTall
          head={
            <tr>
              <th align="left">{i18n.translation.common.name}</th>
              <th align="left">{i18n.translation.common.type}</th>
              <th align="left">{i18n.translation.systems.plural}</th>
              <th>{i18n.translation.common.visibility}</th>
              <th></th>
            </tr>
          }
          items={data?.items}
          renderRow={(item) => (
            <>
              <td>
                <Link
                  to={``}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setItemToEdit(item);
                  }}
                >
                  {item.name}
                </Link>
              </td>
              <td>{formatType(i18n.translation, item.type)}</td>
              <td>{item.systems?.map((s) => s.name).join(", ")}</td>
              <td align="center">
                <Visibility
                  value={item.visibility}
                  onChange={(value) => normativeId && updateVisibility(normativeId, item.id, value)}
                />
              </td>
            </>
          )}
          onRowClick={(item) => setItemToEdit(item)}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            accessesAction(item),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          total={data?.total}
          error={!!error}
        />
      </FlexContent>
      {accessesDialog}
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.itemNormativeExclusions.edit}>
          {itemToEdit?.id && normativeId && (
            <NormativeVersionForm
              item={itemToEdit}
              normativeId={normativeId}
              onClose={() => setItemToEdit(undefined)}
            />
          )}
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.items.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                if (normativeId) {
                  setIsSubmitting(true);
                  normativeVersionsApi
                    .deleteNormativeVersion(normativeId, itemToDelete.id)
                    .then(() => setItemToDelete(undefined))
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }
              },
            },
          ]}
        >
          {i18n.translation.itemNormativeExclusions.questions.remove}
        </ModalDialog>
      )}
    </>
  );
};

export default NormativeVersions;

const formatType = (translation: Translation, type: "anchor-point" | "life-line"): string => {
  switch (type) {
    case "anchor-point":
      return translation.anchorPoints.singular;
    case "life-line":
      return translation.common.lifeLine;
  }
};
